import proj4 from 'proj4';
import { fetchSeasonData } from "./FetchSeasonData.tsx";
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import shp from 'shpjs';
import ReactDOMServer from "react-dom/server";
import OrpPopup from "./OrpPopup.tsx";
import { fixedRanges } from './FixedRanges.tsx';

proj4.defs("EPSG:5514", "+proj=krovak +lat_0=49.5 +lon_0=24.83333333333333 +alpha=30.28813972222222 +k=0.9999 +x_0=0 +y_0=0 +datum=S-JTSK +units=m +no_defs");

// ⛔ zruš toto:
// import chroma from 'chroma-js';
// const scale = chroma.scale(["#CD5B45", "#E69F7D", "#FFE4B5", "#A9B784", "#548B54"]);

// ✅ pridaj toto hore do súboru (napr. pod ostatné importy)
const scaleColors = ["#CD5B45", "#E69F7D", "#FFE4B5", "#A9B784", "#548B54"] as const;

const hexToRgb = (hex: string) => {
    const h = hex.replace('#', '');
    const bigint = parseInt(h.length === 3 ? h.split('').map(c => c + c).join('') : h, 16);
    return { r: (bigint >> 16) & 255, g: (bigint >> 8) & 255, b: bigint & 255 };
};

const rgbToHex = (r: number, g: number, b: number) =>
    '#' + [r, g, b].map(v => v.toString(16).padStart(2, '0')).join('');

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

const mixHex = (c1: string, c2: string, t: number) => {
    const a = hexToRgb(c1);
    const b = hexToRgb(c2);
    const r = Math.round(lerp(a.r, b.r, t));
    const g = Math.round(lerp(a.g, b.g, t));
    const bch = Math.round(lerp(a.b, b.b, t));
    return rgbToHex(r, g, bch);
};

/** vráti farbu pre t∈[0,1] podľa rovnakého prechodu ako Legend (5 stops) */
const gradientColor = (t: number, reverse = false, colors = scaleColors): string => {
    const arr = reverse ? [...colors].reverse() : colors;
    const n = arr.length; // 5
    if (t <= 0) return arr[0];
    if (t >= 1) return arr[n - 1];
    const pos = t * (n - 1);          // 0..4
    const idx = Math.floor(pos);      // 0..3
    const localT = pos - idx;         // 0..1
    return mixHex(arr[idx], arr[idx + 1], localT);
};


const fetchOrpTemperatures = async (year: number, orpName: string) => {
    try {
        const url = `/orp_temperatures/${year}.csv`;
        console.log(`Fetching ORP temperatures from: ${url}`);

        const response = await fetch(url);
        if (!response.ok) {
            console.error(`Failed to fetch ORP CSV for year ${year}:`, response.statusText);
            return { temperatures: [], temperatureDays: [] };
        }

        const text = await response.text();
        console.log('Raw ORP CSV:', text);

        // Split into rows and clean whitespace
        const rows = text.trim().split('\n').map(row => row.trim());
        if (rows.length < 2) {
            console.error(`CSV for ORP ${orpName} in year ${year} is empty or improperly formatted.`);
            return { temperatures: [], temperatureDays: [] };
        }

        // Detect delimiter: ; or ,
        const separator = text.includes(';') ? ';' : ',';
        console.log(`Detected separator: ${separator}`);

        // Parse headers (first row)
        const headers = rows.shift()?.split(separator).map(h => h.trim().replace(/^"|"$/g, '')) || [];
        console.log('Headers:', headers);

        // Ensure the "Date" column is present (first column now)
        if (headers[0] !== "Date") {
            console.error(`CSV for ORP ${orpName} is missing a valid "Date" column.`);
            return { temperatures: [], temperatureDays: [] };
        }

        // Find index of ORP in the header row
        const orpIndex = headers.indexOf(orpName);
        if (orpIndex === -1) {
            console.error(`ORP "${orpName}" not found in CSV headers for year ${year}. Available headers:`, headers);
            return { temperatures: [], temperatureDays: [] };
        }

        // Extract Date (DOY) and Temperature Values
        const temperatures: number[] = [];
        const temperatureDays: number[] = [];

        rows.forEach((row, rowIndex) => {
            const values = row.split(separator).map(v => v.trim().replace(/^"|"$/g, '')); // Remove quotes

            // Skip rows with insufficient columns
            if (values.length <= orpIndex) {
                console.warn(`Skipping malformed row ${rowIndex}: ${row}`);
                return;
            }

            const dateString = values[0]; // Now first column is correctly the date
            console.log(`Row ${rowIndex}: Date = ${dateString}, Temperature = ${values[orpIndex]}`);

            // Convert MM/DD/YYYY to DOY
            const doy = getDOYORP(dateString, year);
            if (!isNaN(doy)) {
                temperatureDays.push(doy);
                temperatures.push(parseFloat(values[orpIndex]) || NaN);
            } else {
                console.warn(`Invalid date string: ${dateString}`);
            }
        });

        console.log(`Extracted ORP Temperatures for ${orpName}:`, temperatures);
        console.log(`Temperature Days (DOY):`, temperatureDays);
        return { temperatures, temperatureDays };

    } catch (error) {
        console.error(`Error processing ORP CSV for year ${year}:`, error);
        return { temperatures: [], temperatureDays: [] };
    }
};

// ✅ Extend fetchRemoteOrpSeasonData to support "combined" product
export const fetchRemoteOrpSeasonData = async (
    orpName: string,
    year: number,
    type: "start" | "end",
    source: "dpz" | "combined" = "dpz"
): Promise<{ doy: number | null; date: string | null }> => {
    const folder = type === "start" ? "start_of_season" : "end_of_season";
    const url = source === "dpz"
        ? `/remote_sensing_orp/${folder}/${year}.csv`
        : `/combined_orp/${folder}/${year}.csv`;

    const normalize = (s: string) =>
        s
            .replace(/_/g, " ") // 🔧 NEW: change underscores to spaces
            .normalize("NFKD")
            .replace(/[^\w\s]/g, "")
            .replace(/\s+/g, " ")
            .trim()
            .toLowerCase();

    try {
        const res = await fetch(url);
        if (!res.ok) throw new Error(`❌ Failed to fetch: ${url}`);
        const text = await res.text();

        const rows = text.trim().split("\n");
        const headers = rows[0].split(",").map(h => h.replace(/^"|"$/g, "").trim());
        const dataRows = rows.slice(1);

        const orpColumnIndex = headers.findIndex(h => normalize(h) === "so orp");
        const doyIndex = headers.findIndex(h => normalize(h) === "doy");
        const dateIndex = headers.findIndex(h => normalize(h) === "date");

        if (orpColumnIndex === -1 || doyIndex === -1) {
            console.error("❌ Missing required columns in CSV:", headers);
            return { doy: null, date: null };
        }

        const normTarget = normalize(orpName);
        for (const row of dataRows) {
            const cols = row.split(",").map(c => c.replace(/^"|"$/g, "").trim());
            const name = cols[orpColumnIndex];
            const normName = normalize(name);

            // DEBUG
            // console.log("🔍 ORP Match Check", { normTarget, normName });

            if (
                normName === normTarget ||
                normName.includes(normTarget) ||
                normTarget.includes(normName)
            ) {
                const doyStr = cols[doyIndex];
                const rawDateStr = dateIndex !== -1 ? cols[dateIndex]?.trim() : null;

                const doy = doyStr?.toUpperCase() === "NA" ? null : parseInt(doyStr, 10);
                let finalDate = rawDateStr;
                if (!finalDate || finalDate.toUpperCase() === "NA") {
                    if (!isNaN(doy ?? NaN)) {
                        const d = new Date(year, 0);
                        d.setDate(d.getDate() + (doy ?? 0));
                        finalDate = d.toISOString().split("T")[0];
                    } else {
                        finalDate = null;
                    }
                }

                return {
                    doy: !isNaN(doy ?? NaN) ? doy : null,
                    date: finalDate,
                };
            }
        }

        console.warn(`⚠️ No matching ORP found in CSV for "${orpName}"`);
        return { doy: null, date: null };
    } catch (err) {
        console.error(`❌ ${source.toUpperCase()} ORP fetch failed for ${orpName}:`, err);
        return { doy: null, date: null };
    }
};


const getDOYORP = (dateString: string, year: number): number => {
    // Ensure the date string includes the year
    const fullDateString = `${year}-${dateString.split('-')[1]}-${dateString.split('-')[2]}`;
    const date = new Date(fullDateString);
    if (isNaN(date.getTime())) {
        console.error(`Invalid date string: ${dateString}`);
        return NaN;
    }
    const startOfYear = new Date(year, 0, 0); // Start of the year
    const diff = date.getTime() - startOfYear.getTime();
    return Math.floor(diff / (1000 * 60 * 60 * 24)); // Convert milliseconds to days
};


const getColorForValue = (
    value: number | null,
    min: number,
    max: number,
    reverse: boolean = false,
    fixedRange?: { min: number; max: number }
): string => {
    if (value === null) return '#D3D3D3';
    const rangeMin = fixedRange ? fixedRange.min : min;
    const rangeMax = fixedRange ? fixedRange.max : max;

    // ochrana proti deleniu nulou
    const denom = rangeMax - rangeMin;
    if (denom <= 0) return gradientColor(0.5, reverse);

    const normalized = (value - rangeMin) / denom;
    const clamped = Math.max(0, Math.min(1, normalized));
    return gradientColor(clamped, reverse);
};



let orpLayer: L.GeoJSON | null = null;

export const loadOrpGeoJson = (
    mapInstance: L.Map,
    selectedYear: number,
    selectedDays: number,
    setAverageTemperatures: (temps: number[], days: number[]) => void,
    checkedItems: { vegetationSeason?: boolean; vegetationSeasonEnd?: boolean; seasonLength?: boolean },
    temperatureThreshold: number,
    setOrpMinMax: (range: { min: number; max: number } | null) => void,
    selectedProduct: string,
    language: 'cz' | 'en'
) => {
    if (orpLayer) {
        mapInstance.removeLayer(orpLayer);
        orpLayer = null;
    }

    shp('/orp_shp/orp_shapefile.zip')
        .then(async (geojsonRaw) => {
            const geojson = geojsonRaw && geojsonRaw.features
                ? geojsonRaw
                : { type: "FeatureCollection", features: geojsonRaw };

            geojson.features.forEach((feature, index) => {
                if (!feature.geometry || !feature.geometry.coordinates) {
                    console.error(`Feature ${index} has invalid geometry:`, feature);
                } else {
                    feature.geometry.coordinates = reprojectCoordinates(feature.geometry.coordinates);
                }
            });

            const isRemote = selectedProduct === "dpz" || selectedProduct === "combinedProduct";
            const sourceType = selectedProduct === "combinedProduct" ? "combined" : "dpz";


            const seasonData = await Promise.all(
                geojson.features.map(async (feature, index) => {
                    const orpName = feature.properties?.NAZ_ORP?.trim();
                    if (!orpName) {
                        console.warn(`❗ Feature ${index} is missing a valid NAZ_ORP`);
                        return {
                            NAZ_ORP: `undefined_${index}`,
                            seasonStart: { doy: null, date: null },
                            seasonEnd: { doy: null, date: null },
                            seasonLength: null,
                            chosenValue: null
                        };
                    }

                    const seasonStart = isRemote
                        ? await fetchRemoteOrpSeasonData(orpName, selectedYear, "start", sourceType)
                        : await fetchSeasonData(orpName, `${temperatureThreshold}C`, selectedYear, selectedDays, "start", "orp");

                    const seasonEnd = isRemote
                        ? await fetchRemoteOrpSeasonData(orpName, selectedYear, "end", sourceType)
                        : await fetchSeasonData(orpName, `${temperatureThreshold}C`, selectedYear, selectedDays, "end", "orp");


                    const seasonLength = seasonStart.doy && seasonEnd.doy
                        ? seasonEnd.doy - seasonStart.doy
                        : null;

                    // ✅ TU vlož log
                    console.log(`✔️ Popup-ready: ${orpName}`, {
                        seasonStart,
                        seasonEnd,
                        seasonLength,
                        checkedItems
                    });

                    let chosenValue: number | null = null;
                    if (checkedItems.vegetationSeason) chosenValue = seasonStart.doy;
                    if (checkedItems.vegetationSeasonEnd) chosenValue = seasonEnd.doy;
                    if (checkedItems.seasonLength) chosenValue = seasonLength;

                    return { NAZ_ORP: orpName, seasonStart, seasonEnd, seasonLength, chosenValue };
                })
            );

            let minValue = Infinity;
            let maxValue = -Infinity;

            seasonData.forEach((data) => {
                if (data.chosenValue !== null) {
                    minValue = Math.min(minValue, data.chosenValue);
                    maxValue = Math.max(maxValue, data.chosenValue);
                }
            });

            if (minValue === Infinity || maxValue === -Infinity) {
                console.warn("No valid season data found. Legend will not be shown.");
                setOrpMinMax(null);
                return;
            } else {
                setOrpMinMax({ min: minValue, max: maxValue });
            }

            orpLayer = L.geoJSON(geojson, {
                style: (feature) => {
                    const name = feature.properties?.NAZ_ORP?.trim();
                    const data = seasonData.find(d => d.NAZ_ORP === name);

                    if (!data || data.chosenValue === null) {
                        return {
                            color: "#000000",
                            weight: 1,
                            fillOpacity: 0.3,
                            fillColor: "#D3D3D3",
                        };
                    }
                    const useFixedRange = selectedProduct === "combinedProduct" || selectedProduct === "dpz";
                    let fixed: { min: number; max: number } | undefined;

                    if (useFixedRange) {
                        if (checkedItems.vegetationSeason) {
                            fixed = fixedRanges[selectedProduct].SOS;
                        } else if (checkedItems.seasonLength) {
                            fixed = fixedRanges[selectedProduct].LOS;
                        } else if (checkedItems.vegetationSeasonEnd) {
                            fixed = fixedRanges[selectedProduct].EOS;
                        }
                    }

                    const color = getColorForValue(
                        data.chosenValue,
                        minValue,
                        maxValue,
                        checkedItems.vegetationSeason,
                        fixed
                    );

                    return {
                        color: "#000000",
                        weight: 1,
                        fillOpacity: 0.7,
                        fillColor: color,
                    };
                },
                onEachFeature: (feature, layer) => {
                    const rawName = feature.properties?.NAZ_ORP?.trim();
                    if (!rawName) return;

                    const season = seasonData.find(d => d.NAZ_ORP === rawName);
                    if (!season) {
                        console.warn(`No season data found for ${rawName}`);
                        return;
                    }

                    layer.on("click", () => {
                        const popupContent = ReactDOMServer.renderToStaticMarkup(
                            <OrpPopup
                                name={rawName}
                                seasonStart={season.seasonStart}
                                seasonEnd={season.seasonEnd}
                                seasonLength={season.seasonLength}
                                checkedItems={checkedItems}
                                language={language} // ✅ pridaj toto
                            />
                        );


                        layer.bindPopup(popupContent).openPopup();

                        fetchOrpTemperatures(selectedYear, rawName).then(({ temperatures, temperatureDays }) => {
                            setAverageTemperatures(temperatures, temperatureDays);
                        });
                    });
                }
            });
            orpLayer.addTo(mapInstance);
        })
        .catch((error) => console.error("❌ Error loading ORP shapefile:", error));
};


const reprojectCoordinate = (coord: [number, number]) =>
    proj4('EPSG:5514', 'EPSG:4326', coord); // EPSG:5514 is S-JTSK

const reprojectCoordinates = (geometry: any): any => {
    if (!geometry || !geometry.type || !geometry.coordinates) return geometry;

    const recurse = (coords: any): any =>
        typeof coords[0] === 'number'
            ? reprojectCoordinate(coords)
            : coords.map(recurse);

    return {
        ...geometry,
        coordinates: recurse(geometry.coordinates),
    };
};

